import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { useAtom, useSetAtom } from "jotai";
import React from "react";
import styled from "styled-components";

import {
  planogramHoveredShelfAtom,
  planogramSelectedShelfAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Color, Flex, Text } from "src/elements";
import { colorToRGBA } from "src/utils/colorToRgba";
import { useTranslation } from "src/i18n";
import { PogAtom } from "../../store/types";
import { Grill } from "./Grill";
import { useShelfContextMenu } from "./useShelfContextMenu";

const shelfRoundness = 3;

const ShelfName = styled(Text)`
  position: absolute;
  top: -5px;
  left: 0;
  padding-left: 8px;
  font-size: 10px;
  color: ${Color.white};
`;

const ShelfBottomElement = styled(Flex)<{
  width: number;
  thickness: number;
  type: PlanogramShelf["type"];
  scaleX: number;
  scaleY: number;
  isNewShelf: boolean;
  isSelected: boolean;
}>`
  position: relative;
  width: ${({ width, scaleX }) => `calc(${width}px * ${scaleX})`};
  height: ${({ thickness, scaleY }) => `calc(${thickness}px * ${scaleY})`};
  border: ${({ type }) => type !== "RISER" && type !== "HANGCELL" && "1px solid black"};
  background: ${({ type, isNewShelf, isSelected }) =>
    (type === "RISER" && colorToRGBA(Color.red, 0.5)) ||
    (isNewShelf && Color.primary) ||
    (isSelected && Color.pureYellow) ||
    "var(--shelf-color)"};
  box-shadow: ${({ type }) =>
    type !== "RISER" &&
    type !== "HANGCELL" &&
    `0px 6.8px 17px 0px rgba(179, 186, 197, 0.8),
    0px 2.7px 2.7px 0px rgba(77, 77, 77, 0.75),
    0px 1.3px 5.4px 0px rgba(64, 64, 64, 0.75) inset`};

  &:first-of-type {
    border-top-left-radius: ${shelfRoundness}px;
    border-bottom-left-radius: ${shelfRoundness}px;
  }

  &:last-of-type {
    border-top-right-radius: ${shelfRoundness}px;
    border-bottom-right-radius: ${shelfRoundness}px;
  }

  pointer-events: all;
`;

interface Props {
  shelf: PlanogramShelf;
  scaleX: number;
  scaleY: number;
  isSelected: boolean;
  showShelfName: boolean;
  shelfLabelBayNo?: number;
  pogAtom: PogAtom;
  setIsModified?: (modified: boolean) => void;
}

export const getShelfLabel = (
  shelf: Pick<PlanogramShelf, "getNo" | "getName" | "getNotchNo">,
  shelfLabelBayNo?: number,
  t?: (key: string, options?: Record<string, string | number>) => string,
) => {
  const notchSuffix = t
    ? t("shelfNotchPrefix", { notch: shelf.getNotchNo() })
    : ` N:${shelf.getNotchNo()}`;

  if (typeof shelfLabelBayNo === "number") {
    return `${shelfLabelBayNo}.${shelf.getNo()}${notchSuffix}`;
  }

  return `${shelf.getName()}${notchSuffix}`;
};

export const ShelfBottom = ({
  shelf,
  scaleX,
  scaleY,
  isSelected,
  pogAtom,
  showShelfName,
  shelfLabelBayNo,
  setIsModified,
}: Props) => {
  const { t } = useTranslation("common");
  const [selectedShelf] = useAtom(planogramSelectedShelfAtom);
  const setHoveredShelf = useSetAtom(planogramHoveredShelfAtom);
  const openShelfContextMenu = useShelfContextMenu(pogAtom, setIsModified);

  const onPointerEnter = () => {
    setHoveredShelf(shelf);
  };

  const onPointerLeave = () => {
    setHoveredShelf(null);
  };

  const onShelfMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    openShelfContextMenu(shelf, event);
  };

  return (
    <ShelfBottomElement
      role="group"
      aria-label={t("shelfName", { name: shelf.getName() })}
      width={shelf.width}
      thickness={shelf.thickness}
      type={shelf.type}
      scaleX={scaleX}
      scaleY={scaleY}
      isNewShelf={shelf.uniqueId === selectedShelf?.uniqueId && selectedShelf.isNew === true}
      isSelected={isSelected}
      onContextMenu={onShelfMenu}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <Grill height={shelf.grillHeight} scaleY={scaleY} />
      {showShelfName && (
        <ShelfName color={Color.white} variant="caption1">
          {t("shelf")}: {getShelfLabel(shelf, shelfLabelBayNo, t)}
        </ShelfName>
      )}
    </ShelfBottomElement>
  );
};
